import { makeAutoObservable, runInAction } from 'mobx'
import { formatPhoneNumberNational, logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import type { IParamsCreateNumbersLandline } from 'entities/Phone/api/types'
import { PhoneApi } from 'entities/Phone'
import { Phone } from 'entities/Phone/model/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export type IVerifyNumberStoreConfig = {
  fetchNumbersLandline: (number: string, update?: boolean) => Promise<Phone | { error: string }>
  getPhone: () => Phone | null
  getPayload: () => IParamsCreateNumbersLandline
  setPhone: (phone: Phone) => void
  syncData: (phone: Phone) => void
  initStep: (phone: Phone) => void
}

export class VerifyNumberStore {
  private _error = ''
  private _extension = ''
  private _verificationCode = ''
  private _loadingVerify = false
  private _loadingResend = false
  private _loadingCheck = false
  private _timerInterval: ReturnType<typeof setInterval> | null = null
  private _recheckInterval: ReturnType<typeof setInterval> | null = null
  private _seconds = 60000
  private _showRetry = false
  private _canVerify = false

  constructor(private _config: IVerifyNumberStoreConfig, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get error() {
    return this._error
  }

  get showRetry() {
    return this._showRetry
  }

  get seconds() {
    return this._seconds
  }

  get secondsTime() {
    return uiStore.dayjs(this._seconds).format('m:ss')
  }

  get verificationCode() {
    return this._verificationCode
  }

  get phone() {
    return this._config.getPhone()
  }

  get phoneNumber() {
    const payload = this._config.getPayload()
    return formatPhoneNumberNational(payload.phone || '')
  }

  get title() {
    if (this._verificationCode) {
      return `We've just placed a call to the landline at ${this.phoneNumber}. Now, all you need to do is listen for the code and enter it to continue`
    }

    return 'Please enter your landline number. We will check if it is eligible for text enabling and call the number to verify it.'
  }

  get payload(): Pick<IParamsCreateNumbersLandline, 'extension'> {
    return {
      extension: this._extension,
    }
  }

  get extension() {
    return this._extension
  }

  get nextDisabled() {
    if (!this._verificationCode) {
      if (this._loadingVerify) return true
      if (this._canVerify) return false

      return false
    }

    return false
  }

  get nextLoading() {
    if (!this._verificationCode) return Boolean(this._loadingVerify)

    return Boolean(this._loadingResend || this._loadingCheck)
  }

  syncData = (phone: Phone) => {
    this._canVerify = Boolean(phone.landline?.can_verify)
  }

  initTimer = () => {
    this._showRetry = false
    this._seconds = 60000

    this._timerInterval && clearInterval(this._timerInterval)
    this._timerInterval = setInterval(() => {
      runInAction(() => {
        this._seconds -= 1000
      })

      if (this._seconds <= 0) {
        this._timerInterval && clearInterval(this._timerInterval)
        this._showRetry = true
      }
    }, 1000)

    this._loadingCheck = true
    this._recheckInterval && clearInterval(this._recheckInterval)
    this._recheckInterval = setInterval(() => {
      this.onCheckNumber()
    }, 5000)
  }

  onCheckNumber = async () => {
    if (!this.phone) return

    try {
      const data = await this._config.fetchNumbersLandline(this.phone.number, false)

      if (data instanceof Phone) {
        if (this.phone.landline?.status !== data.landline?.status) {
          this._config.syncData(data)
          this._config.initStep(data)
        }
      }
    } catch (e) {
      logger.error(e)
    }
  }

  onResendNewCode = async () => {
    runInAction(() => {
      this._loadingResend = true
      this._showRetry = false
      this._seconds = 15000
    })

    await this.getVerificationCode()
    await this.onCheckNumber()
    this.initTimer()

    runInAction(() => {
      this._loadingResend = false
    })
  }

  setExtension = (value: string) => {
    const valueExtension = value.replace(/[^0-9]/g, '')

    if (valueExtension.length > 3) return

    this._extension = valueExtension
  }

  disposeStep = () => {
    this._error = ''
    this._loadingVerify = false
    this._loadingResend = false
    this._loadingCheck = false
    this._seconds = 60000
    this._showRetry = false
    this._timerInterval && clearInterval(this._timerInterval)
    this._recheckInterval && clearInterval(this._recheckInterval)
  }

  dispose = () => {
    this._extension = ''
    this.disposeStep()
  }

  getVerificationCode = async () => {
    try {
      runInAction(() => {
        this._error = ''
      })
      const payload = this._config.getPayload()

      const response = await PhoneApi.createNumbersLandlineVerifyRequest(payload)
      const data = response.data

      if ('error' in data) {
        runInAction(() => {
          this._error = data.error
        })

        return {
          error: data.error,
        }
      }

      const { verification_code, number } = data

      runInAction(() => {
        this._verificationCode = verification_code
        this._config.setPhone(new Phone(number, this._entities))
      })
    } catch (e) {
      logger.error(e)
    }
  }

  nextClickHandler = async () => {
    try {
      runInAction(() => {
        this._loadingVerify = true
      })

      await this.getVerificationCode()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingVerify = false
      })
    }
  }
}
